import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Clock, AlertTriangle } from "lucide-react";
import AuditResponseModal from "./AuditResponseModal";
import Badge from "../../../components/common/Badge";
import { statusVariant } from "../../../utils/statusVariant";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import { findingService, Finding } from "../../../services/auditService";
import QueriesModule from "../../audit/QueriesModule";

interface Task {
  id: number;
  type: string;
  title: string;
  dueDate: string;
  status: string;
  priority: string;
  context: string;
  description: string;
}

interface WorkAuditComplianceBodyProps {
  filter: "audit" | "compliance";
  tasks: Task[];
  onSelectTask: (task: Task) => void;
  /** 'active' (default) = Action Centre, unchanged. 'archive' = History: only
   *  closed/completed audit items. */
  scope?: "active" | "archive";
  /** When true, hide every mutating control. Row click (view) still fires. */
  readOnly?: boolean;
}

// Adapted audit tasks carry a free-text status string. Treat these as closed.
const isClosedAuditTask = (status?: string | null): boolean => {
  if (!status) return false;
  return ["DONE", "VERIFIED", "ARCHIVED", "CANCELLED", "REJECTED"].includes(
    status.toUpperCase(),
  );
};


const PAGE_SIZE = 10;

const WorkAuditComplianceBody: React.FC<WorkAuditComplianceBodyProps> = ({
  filter,
  tasks,
  onSelectTask,
  scope = "active",
  readOnly = false,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [openFindingId, setOpenFindingId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [commsCount, setCommsCount] = useState(0);

  useEffect(() => {
    if (filter !== "audit" || scope !== "active") return;
    let cancelled = false;
    findingService.allocatedToMe(false)
      .then((r) => { if (!cancelled) setFindings(r.data ?? []); })
      .catch(() => { if (!cancelled) setFindings([]); });
    return () => { cancelled = true; };
  }, [filter, scope]);

  // Deep-link from calendar / inbox: /work?tab=audit&open=<finding_id> →
  // open the (temporary) audit-finding response model. The response modal is a
  // mutating affordance, so in read-only (History) we never auto-open it.
  useEffect(() => {
    if (filter !== "audit" || readOnly) return;
    const o = searchParams.get("open");
    if (!o) return;
    const id = parseInt(o, 10);
    if (Number.isFinite(id)) setOpenFindingId(id);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [filter, readOnly, searchParams, setSearchParams]);

  const displayLabel = filter === "audit" ? "Audit" : "Compliance";

  // History scope shows only closed/completed audit items; active is unchanged.
  const scoped =
    scope === "archive" ? tasks.filter((t) => isClosedAuditTask(t.status)) : tasks;

  const filtered = scoped;

  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // The audit tab also surfaces findings + open communications below the task
  // list. When either exists, the big "No Audit Items Found" empty state is
  // misleading (there ARE items) — suppress it and let those sections show.
  const hasAuditExtras =
    filter === "audit" && scope === "active" && (findings.length > 0 || commsCount > 0);

  return (
    <div className="netbody-card flex flex-col h-full">

      <div className="netbody-content">
        <div className="scroll-container">
          {filter === "audit" && scope === "active" && findings.length > 0 && (
            <div className="audit-findings-section">
              <h4 className="section-label">Findings allocated to me</h4>
              <div className="action-card-grid">
                {findings.map((f) => (
                  <div
                    key={f.id}
                    className="action-card"
                    onClick={() => setOpenFindingId(f.id)}
                  >
                    <div className="action-card__header">
                      <div className="action-card__icon">
                        <AlertTriangle size={16} />
                      </div>
                      <div className="action-card__title-block">
                        <span className="action-card__title">{f.findings_text}</span>
                        <span className="action-card__subtitle">Finding</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {filter === "audit" && scope === "active" && (
            <div className="audit-comms-section">
              <QueriesModule channelLock="external" recipientScope onCountChange={setCommsCount} />
            </div>
          )}

          {filtered.length === 0 && !(filter === "audit" && scope === "active") ? (
            <NoDataFound
              title={`No ${displayLabel} Items Found`}
              message={`There are no ${displayLabel.toLowerCase()} items assigned to you.`}
            />
          ) : filtered.length > 0 ? (
            <div className="action-card-grid">
              {paginated.map((task) => (
                <div
                  key={task.id}
                  className="action-card"
                  onClick={() => onSelectTask(task)}
                >
                  <div className="action-card__header">
                    <div className="action-card__title-block">
                      <span className="action-card__title">{task.title}</span>
                      <span className="action-card__subtitle">
                        {task.type.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div className="action-card__badge">
                      <Badge
                        variant={statusVariant(task.status)}
                        size="sm"
                      >
                        {task.status}
                      </Badge>
                    </div>
                  </div>

                  <div className="action-card__meta">
                    {task.context && (
                      <span className="action-card__meta-item">
                        <MapPin size={12} />
                        {task.context}
                      </span>
                    )}
                    <span
                      className={`action-card__meta-item${task.status === "Overdue" ? " action-card__meta-item--overdue" : ""}`}
                    >
                      <Clock size={12} />
                      Due: {task.dueDate}
                      {task.status === "Overdue" && (
                        <Badge variant="danger" size="sm">OVERDUE</Badge>
                      )}
                    </span>
                  </div>

                  {task.description && (
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                      {task.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          {filtered.length > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={filtered.length}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              recordLabel={`${displayLabel.toLowerCase()} items`}
            />
          )}
        </div>
      </div>

      {openFindingId !== null && (
        <AuditResponseModal findingId={openFindingId} onClose={() => setOpenFindingId(null)} />
      )}
    </div>
  );
};

export default WorkAuditComplianceBody;
